import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DeliverySettings, DeliveryWindow } from '@/api/suppliers'
import { Button } from '@/components/ui'
import { describeDeliveryDays } from '@/features/delivery/days'
import { formatDate } from '@/lib/format'

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]

/** Επιλογή ημερών με κουμπάκια: σε κινητό είναι ασύγκριτα γρηγορότερη από λίστα. */
export function WeekdayPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: number[]
  onChange: (days: number[]) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAYS.map((day) => {
        const selected = value.includes(day)
        return (
          <button
            key={day}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() =>
              onChange(selected ? value.filter((d) => d !== day) : [...value, day].sort())
            }
            className={
              'h-10 w-10 rounded-full text-sm font-semibold transition ' +
              (selected
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200') +
              (disabled ? ' opacity-50' : '')
            }
          >
            {t(`weekdaysShort.${day}`)}
          </button>
        )
      })}
    </div>
  )
}

/** «Απομένουν 2 ώρες 15 λεπτά» — η μονάδα κόβεται στο λεπτό, όχι στο δευτερόλεπτο. */
function formatRemaining(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}`
  return `${minutes}′`
}

/**
 * Η αντίστροφη μέτρηση προς το cutoff.
 *
 * Μετρά τοπικά από το `secondsToCutoff` που έδωσε ο διακομιστής, αντί να
 * υπολογίζει από το ρολόι του κινητού: το ρολόι του χρήστη μπορεί να αποκλίνει
 * λεπτά ή και ώρες, και μια λάθος μέτρηση εδώ σημαίνει χαμένη παράδοση.
 */
export function CutoffCountdown({ window }: { window: DeliveryWindow }) {
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState(window.secondsToCutoff)

  useEffect(() => {
    setRemaining(window.secondsToCutoff)
    if (window.secondsToCutoff === null) return
    const timer = setInterval(
      () => setRemaining((value) => (value === null ? null : Math.max(0, value - 30))),
      30_000,
    )
    return () => clearInterval(timer)
  }, [window.secondsToCutoff])

  if (window.cutoffTime === null) return null

  if (window.cutoffPassed || remaining === 0) {
    return (
      <span className="inline-flex items-center rounded-lg bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
        {t('delivery.passed')}
      </span>
    )
  }

  // Κάτω από δύο ώρες η ένδειξη γίνεται επείγουσα: εκεί χάνονται οι παραδόσεις.
  const urgent = remaining !== null && remaining < 7200
  return (
    <span
      className={
        'inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ' +
        (urgent ? 'bg-amber-50 text-amber-800' : 'bg-brand-50 text-brand-700')
      }
    >
      {t('delivery.cutoffAt', { time: window.cutoffTime.slice(0, 5) })}
      {remaining !== null && ` · ${t('delivery.remaining', { time: formatRemaining(remaining) })}`}
    </span>
  )
}

/** Σύνοψη του παραθύρου παράδοσης, χωρίς δυνατότητα αλλαγής. */
export function DeliverySummary({ window }: { window: DeliveryWindow }) {
  const { t } = useTranslation()
  const days = describeDeliveryDays(window.deliveryDays, t)

  return (
    <div className="flex flex-col gap-1.5 text-sm text-gray-600">
      <CutoffCountdown window={window} />
      <p>{t('delivery.deliversOn', { days })}</p>
      <p>
        {t('delivery.earliest')}: <strong>{formatDate(window.earliestDate)}</strong>
      </p>
    </div>
  )
}

/** Φόρμα ρύθμισης. Χρησιμοποιείται και από τον πελάτη και από το portal. */
export function DeliverySettingsForm({
  window,
  onSave,
  saving,
  children,
}: {
  window: DeliveryWindow | null
  onSave: (settings: DeliverySettings) => void
  saving: boolean
  children?: React.ReactNode
}) {
  const { t } = useTranslation()
  const [cutoff, setCutoff] = useState(window?.cutoffTime?.slice(0, 5) ?? '')
  const [days, setDays] = useState<number[]>(window?.deliveryDays ?? [])
  const [leadTime, setLeadTime] = useState(String(window?.leadTimeDays ?? 0))

  return (
    <form
      className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4"
      onSubmit={(event) => {
        event.preventDefault()
        onSave({
          cutoffTime: cutoff ? `${cutoff}:00` : null,
          deliveryDays: days,
          leadTimeDays: Number(leadTime) || 0,
        })
      }}
    >
      <h2 className="font-semibold text-gray-900">{t('delivery.title')}</h2>

      <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
        {t('delivery.cutoffTime')}
        <input
          type="time"
          value={cutoff}
          onChange={(event) => setCutoff(event.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-gray-900"
        />
        <span className="text-xs font-normal text-gray-500">{t('delivery.cutoffHint')}</span>
      </label>

      <div className="flex flex-col gap-2 text-sm font-medium text-gray-700">
        {t('delivery.deliveryDays')}
        <WeekdayPicker value={days} onChange={setDays} />
        {days.length === 0 && (
          <span className="text-xs font-normal text-gray-500">{t('delivery.everyDay')}</span>
        )}
      </div>

      <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
        {t('delivery.leadTimeDays')}
        <input
          type="number"
          min={0}
          max={30}
          value={leadTime}
          onChange={(event) => setLeadTime(event.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-gray-900"
        />
        <span className="text-xs font-normal text-gray-500">{t('delivery.leadTimeHint')}</span>
      </label>

      {children}

      <Button type="submit" loading={saving}>
        {t('common.save')}
      </Button>
    </form>
  )
}
